import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FileAttachment04, Pencil02, Trash01 } from 'untitledui-js-base';
import { useCommonApi } from '../api';
import { formatPrice } from '../utils/formatPrice';
import { PageLayout } from './common';
import { NoDataFound } from './common/NoDataFound';
import { RemoveCommonDialog } from './common/RemoveCommonDialog';
import { ExpenseDocumentEditDialog } from './sourceCommissions/ExpenseDocumentEditDialog';
import { SourceNameCell } from './sourceCommissions/SourceNameCell';

const DOCUMENTS_LIMIT = 20;

export const ExpenseDocumentsPage = () => {
  const api = useCommonApi();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const [editingId, setEditingId] = useState<number>();
  const [removingId, setRemovingId] = useState<number>();

  const { data, isPending, isError, refetch } = useQuery(
    api.getSourceCommissionDocuments({ limit: DOCUMENTS_LIMIT, offset: (page - 1) * DOCUMENTS_LIMIT })
  );
  const { mutate: remove, isPending: isRemoving } = useMutation(api.removeSourceCommissionDocument());

  const setPage = (value: number) => setSearchParams({ page: String(value) });
  const hasNext = (data?.length ?? 0) >= DOCUMENTS_LIMIT;

  const onRemove = () => {
    if (removingId === undefined) return;
    remove({ id: removingId }, { onSettled: () => setRemovingId(undefined) });
  };

  return (
    <PageLayout title="Výdajové doklady" withContentBlock>
      {isPending && (
        <Stack alignItems="center" p={4}>
          <CircularProgress />
        </Stack>
      )}
      {isError && (
        <Alert severity="error" action={<Button onClick={() => refetch()}>Zkusit znovu</Button>}>
          Nepodařilo se načíst doklady.
        </Alert>
      )}
      {data && data.length === 0 && <NoDataFound />}
      {data && data.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Číslo</TableCell>
                <TableCell>Tipař</TableCell>
                <TableCell align="right">Celková částka</TableCell>
                <TableCell>Datum</TableCell>
                <TableCell align="right">Položek</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>{doc.documentNumber}</TableCell>
                  <TableCell>
                    <SourceNameCell name={doc.sourceName} tradeName={doc.sourceTradeName} />
                  </TableCell>
                  <TableCell align="right">{formatPrice(doc.totalPrice, 2)}</TableCell>
                  <TableCell>{doc.issueDate}</TableCell>
                  <TableCell align="right">{doc.itemsCount}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {doc.pdf && (
                      <Link href={doc.pdf} target="_blank" underline="always" sx={{ mr: 1 }}>
                        <Stack component="span" direction="row" alignItems="center" gap={0.5} display="inline-flex">
                          <FileAttachment04 size="14px" /> Stáhnout PDF
                        </Stack>
                      </Link>
                    )}
                    <IconButton aria-label="Upravit" onClick={() => setEditingId(doc.id)}>
                      <Pencil02 size="18px" />
                    </IconButton>
                    <IconButton aria-label="Smazat" onClick={() => setRemovingId(doc.id)}>
                      <Trash01 size="18px" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {(page > 1 || hasNext) && (
        <Stack direction="row" justifyContent="center" gap={2} pt={2}>
          <Button disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Předchozí
          </Button>
          <Button disabled={!hasNext} onClick={() => setPage(page + 1)}>
            Další
          </Button>
        </Stack>
      )}
      {page > 1 && data?.length === 0 && (
        <Stack alignItems="center" pt={2}>
          <Button onClick={() => setPage(1)}>Zpět na první stránku</Button>
        </Stack>
      )}

      <ExpenseDocumentEditDialog documentId={editingId} onClose={() => setEditingId(undefined)} />
      <RemoveCommonDialog
        open={removingId !== undefined}
        title="Smazat výdajový doklad"
        isLoading={isRemoving}
        onRemove={onRemove}
        onClose={() => setRemovingId(undefined)}
      />
    </PageLayout>
  );
};
